import React, { useEffect, useRef, useState } from "react";

// TV Millionario's DOBLAR (double up), as played in
// https://www.youtube.com/watch?v=3VRlTIPI0To (0:50-0:59). After a line win
// the reels offer TOMAR O DOBLAR; DOBLAR brings up this screen over the
// reels (the footer stays). Positions were found by registering the video
// onto the 800x600 stage (crop 37,49 816x380 at 854x480) and template-
// matching public/tv-milionario/bonus/dobra-bonus.
//
// Five steps, X2 X4 X8 X16 X50 of the win. Each step's top screen holds a
// card; MAYOR or MENOR bets the card dealt into the screen below beats it
// or is beaten by it, ranked K > Q > J > 10 > ... > 2 > A. A miss loses the
// lot; a hit moves on to the next step, and TOMAR banks what's won so far.

const DO = `${process.env.PUBLIC_URL}/tv-milionario/bonus/dobra-bonus`;
const REF = `${DO}/refactored`;

const COL_X = [35, 182, 329, 477, 624];
const TOP_Y = 164;          // _do_tv (142x132)
const BOTTOM_Y = 322;       // _do_abre (142x152): the closed screen + stand
const BOTTOM_TV_Y = 338;    // _do_tv once the bottom card is up
const FACE_DX = 15;         // card face (111x81) inside a _do_tv
const FACE_DY = 10;
const HALF_GAP = 44;        // the dealt card's top half, under the step card's
const BADGE_DX = 7;         // WIN / LOSE (128x75), over the two halves
const BADGE_DY = 50;
const MOLD_DX = -9;         // the selector frame (160x150) round a bottom screen
const MOLD_DY = 6;

export const MULTIPLIERS = [2, 4, 8, 16, 50];
// _do_mold sprites are green, blue, red, yellow; the video cycles blue,
// green, yellow, red, a colour every 0.25s
const MOLD_ORDER = [1, 0, 3, 2];
const MOLD_MS = 250;
const MSG_MS = 750;         // SELECCIONAR MAYOR O MENOR <-> PRESIONE DOBLAR
const FLIP_FRAME_MS = 70;   // _do_abre 1..4: the screen turns to show the card
const AFTER_WIN_MS = 1200;  // then the next step lights up
const AFTER_LOSE_MS = 1500; // then back to the reels (PIERDE)

const SUITS = ["Club", "Diamond", "Heart", "Spade"];
const RED = { Diamond: true, Heart: true };
const RANK_NAME = { 1: "A", 11: "J", 12: "Q", 13: "K" };
const randomCard = () => ({ rank: 1 + Math.floor(Math.random() * 13), suit: SUITS[Math.floor(Math.random() * 4)] });

// a card face (full 111x81, or the top half 111x46): picture cards are one
// image, the rest are the suit art with the rank laid in the corner
const Face = ({ card, x, y, half }) => {
    const name = RANK_NAME[card.rank] || String(card.rank);
    const abs = { position: "absolute", left: x, top: y, pointerEvents: "none" };
    if (card.rank >= 11) {
        return <img alt="" style={abs} src={`${REF}/_do_jqk${half ? "_b" : ""}/${name}-${card.suit}.png`} />;
    }
    const glyphDir = card.rank === 1 || card.rank === 10 ? "_do_a_10" : "_do_2_9";
    return (
        <>
            <img alt="" style={abs} src={`${REF}/_do_naipe${half ? "_b" : ""}/${card.suit}.png`} />
            <img alt="" style={abs} src={`${REF}/${glyphDir}/${name}-${RED[card.suit] ? "Red" : "Black"}.png`} />
        </>
    );
};

export const preloadDoubleUp = () => {
    const srcs = [`${DO}/DO_MAIN.png`, `${DO}/DO_SEQU.png`, `${DO}/DO_TJ.png`, `${REF}/_do_w_l/Win.png`, `${REF}/_do_w_l/Lose.png`];
    for (let f = 0; f < 5; f++) srcs.push(`${REF}/_do_abre/sprite_${f}.png`);
    for (let f = 0; f < 4; f++) srcs.push(`${REF}/_do_mold/sprite_${f}.png`);
    srcs.push(`${REF}/_do_tv/sprite_1.png`);
    SUITS.forEach((s) => {
        srcs.push(`${REF}/_do_naipe/${s}.png`, `${REF}/_do_naipe_b/${s}.png`);
        ["J", "Q", "K"].forEach((r) => srcs.push(`${REF}/_do_jqk/${r}-${s}.png`, `${REF}/_do_jqk_b/${r}-${s}.png`));
    });
    srcs.forEach((s) => { const im = new Image(); im.src = s; });
};

// amount: the win being doubled. Button: the footer button component of the
// TV game. onAmount / onMessage drive the footer's GANA and message;
// onDone(amount) hands back what's paid (0 on a miss).
const DoubleUp = ({ amount, Button, keyboardInput, onAmount, onMessage, onDone }) => {
    const [refs] = useState(() => MULTIPLIERS.map(randomCard));
    const [dealt, setDealt] = useState([]);       // the bottom cards so far
    const [results, setResults] = useState([]);   // "win" | "lose" per step
    const [step, setStep] = useState(0);
    const [phase, setPhase] = useState("choose"); // choose | flip | shown | done
    const [flipAt, setFlipAt] = useState(0);
    const [now, setNow] = useState(Date.now());
    const alive = useRef(true);
    const state = useRef({});
    state.current = { step, phase };

    const banked = step > 0 ? amount * MULTIPLIERS[step - 1] : amount;

    useEffect(() => {
        alive.current = true;
        const id = setInterval(() => setNow(Date.now()), 33);
        return () => { alive.current = false; clearInterval(id); };
    }, []);

    useEffect(() => { onAmount(banked); }, [banked]);

    // the footer message alternates while a choice is open
    const choosing = phase === "choose";
    useEffect(() => {
        if (!choosing) return;
        let k = 0;
        onMessage("major_minor");
        const id = setInterval(() => onMessage(++k % 2 ? "double" : "major_minor"), MSG_MS);
        return () => clearInterval(id);
    }, [choosing]);

    const later = (ms, fn) => setTimeout(() => { if (alive.current) fn(); }, ms);

    const choose = (higher) => {
        if (state.current.phase !== "choose") return;
        const s = state.current.step;
        const card = randomCard();
        const ref = refs[s];
        // a tie is a miss
        const won = higher ? card.rank > ref.rank : card.rank < ref.rank;
        setPhase("flip");
        setFlipAt(Date.now());
        later(4 * FLIP_FRAME_MS, () => {
            setDealt((d) => [...d, card]);
            setResults((r) => [...r, won ? "win" : "lose"]);
            setPhase("shown");
            if (!won) {
                onAmount(0);
                onMessage("double");
                later(AFTER_LOSE_MS, () => { setPhase("done"); onDone(0); });
            } else if (s === MULTIPLIERS.length - 1) {
                onAmount(amount * MULTIPLIERS[s]);
                onMessage("win");
                later(AFTER_WIN_MS, () => { setPhase("done"); onDone(amount * MULTIPLIERS[s]); });
            } else {
                onMessage("win");
                later(AFTER_WIN_MS, () => { setStep(s + 1); setPhase("choose"); });
            }
        });
    };

    const take = () => {
        if (state.current.phase !== "choose") return;
        setPhase("done");
        onDone(banked);
    };

    // same keys as the reels: "b" (INICIAR) takes; "y" / "n" for MAYOR / MENOR
    useEffect(() => {
        if (!keyboardInput) return;
        const onKey = (e) => {
            const k = e.key.toLowerCase();
            if (k === "y") choose(true);
            else if (k === "n") choose(false);
            else if (k === "b") take();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [keyboardInput]);

    const abs = (style) => ({ position: "absolute", display: "block", pointerEvents: "none", ...style });

    return (
        <div className="tvm-double">
            <img alt="" src={`${DO}/DO_MAIN.png`} style={abs({ left: 0, top: 0, width: 800, height: 488 })} />
            <img alt="" src={`${DO}/DO_SEQU.png`} style={abs({ left: 199, top: 304 })} />

            {COL_X.map((x, c) => {
                const result = results[c];
                const active = c === step && phase !== "done";
                const flipping = c === step && phase === "flip";
                const flipFrame = Math.min(4, 1 + Math.floor((now - flipAt) / FLIP_FRAME_MS));
                return (
                    <React.Fragment key={c}>
                        {/* top: the step's card, or both halves and the verdict */}
                        <img alt="" src={`${REF}/_do_tv/sprite_1.png`} style={abs({ left: x, top: TOP_Y })} />
                        {result ? (
                            <>
                                <Face card={refs[c]} x={x + FACE_DX} y={TOP_Y + FACE_DY} half />
                                <Face card={dealt[c]} x={x + FACE_DX} y={TOP_Y + FACE_DY + HALF_GAP} half />
                                <img alt="" src={`${REF}/_do_w_l/${result === "win" ? "Win" : "Lose"}.png`}
                                    style={abs({ left: x + BADGE_DX, top: TOP_Y + BADGE_DY })} />
                            </>
                        ) : (
                            <>
                                <Face card={refs[c]} x={x + FACE_DX} y={TOP_Y + FACE_DY} />
                                {!active && <img alt="" src={`${DO}/DO_TJ.png`} style={abs({ left: x + FACE_DX, top: TOP_Y + FACE_DY })} />}
                            </>
                        )}

                        {/* bottom: closed, turning, or the dealt card */}
                        {dealt[c] ? (
                            <>
                                <img alt="" src={`${REF}/_do_tv/sprite_1.png`} style={abs({ left: x, top: BOTTOM_TV_Y })} />
                                <Face card={dealt[c]} x={x + FACE_DX} y={BOTTOM_TV_Y + FACE_DY} />
                            </>
                        ) : (
                            <img alt="" src={`${REF}/_do_abre/sprite_${flipping ? flipFrame : 0}.png`} style={abs({ left: x, top: BOTTOM_Y })} />
                        )}
                        {active && (
                            <img alt="" src={`${REF}/_do_mold/sprite_${MOLD_ORDER[Math.floor(now / MOLD_MS) % 4]}.png`}
                                style={abs({ left: x + MOLD_DX, top: BOTTOM_Y + MOLD_DY })} />
                        )}
                    </React.Fragment>
                );
            })}

            {/* the footer row while doubling: MENU AYUDA... become MAYOR DOBLAR MENOR */}
            <Button name="bt_menu" x={23} disabled />
            <Button name="bt_major" x={132} onClick={() => choose(true)} disabled={!choosing} />
            <Button name="bt_db" x={241} disabled />
            <Button name="bt_minor" x={350} onClick={() => choose(false)} disabled={!choosing} />
            <Button name="bt_bet" x={459} disabled />
            <Button name="bt_quick" x={568} disabled />
            <Button name="bt_take" x={678} onClick={take} disabled={!choosing} />
        </div>
    );
};

export default DoubleUp;
